import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  ShapeUtils,
  Vector2,
  type MeshStandardMaterial,
} from "three";

import {
  isSuppressedAuthoredGenericStructureId,
  isSuppressedGelezinioVilkoGenericStructureSegment,
} from "../../config/buildingPassages.ts";
import {
  authoredLandmarkCollisionForBuilding,
  type AuthoredLandmarkCollider,
  type AuthoredLandmarkCollisionSpec,
} from "../../config/landmarks.ts";
import {
  VILNIUS_CIVIC_SQUARE_REPLACED_HARDSCAPE_IDS,
} from "../../config/civicSquares.ts";
import type { StreetSurfaceFamily } from "../../config/streets.ts";
import { TREE_RENDER_CONFIG } from "../../config/trees.ts";
import {
  MAPPED_ROAD_VEHICLE_FAMILY_PROFILES,
  ROAD_VEHICLE_TUNING,
  staticParkedVehicleCollisionsForTile,
  VILNIUS_ROAD_VEHICLES,
  type ParkedVehicleCollisionSpec,
  type RoadVehicleSpec,
} from "../../config/vehicles.ts";
import type {
  RuntimeAreaFeature,
  RuntimeLinearFeature,
  RuntimeParkedVehicleFamily,
  RuntimeParkedVehicleDescriptor,
  RuntimeParkingOrientation,
  RuntimeTreeInstance,
} from "../../world/schema/runtimeTile.ts";
import type { AuthoredBridgeCollisionSpec } from "../../world/bridgeCollisions.ts";
import type { TerrainTileData, WorldTileId } from "../../world/schema/tile.ts";
import type { BuildingFootprintData } from "../../world/schema/tile.ts";
import type { PolygonWithHoles } from "../../world/geometry.ts";
import {
  createStreetWalkableColliderMesh,
  streetFeatureWidthM,
  streetSurfaceFamilyForTile,
} from "../../world/streets.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface UrbanStructureBatch {
  readonly object: Group;
  readonly structureCount: number;
  readonly hardscapePolygonCount: number;
  readonly mappedParkedVehicleCount: number;
  readonly mappedRoadsideParkedVehicleCount: number;
}

export interface MappedParkedVehiclePlacement {
  readonly id: string;
  readonly tileId: WorldTileId;
  readonly sourceId: string;
  readonly sourceFeatureId: string;
  readonly provenance: RuntimeParkedVehicleDescriptor["provenance"];
  readonly centre: Readonly<{ x: number; z: number }>;
  readonly direction: Readonly<{ x: number; z: number }>;
  readonly groundY: number;
  readonly pitchRadians: number;
  readonly bodyColorHex: number;
  readonly family: RuntimeParkedVehicleFamily;
  readonly orientation: RuntimeParkingOrientation;
  readonly roadside: boolean;
}

type HardscapeMaterialGroup =
  | "pedestrian"
  | "carriageway"
  | "unpaved"
  | "marking";

const SURFACE_LIFT_M = 0.035;
const ROAD_MARKING_LIFT_M = 0.07;
const STRUCTURE_BASE_SINK_M = 0.08;
const UNNAMED_PARKING_ROAD_MAX_DISTANCE_M = 3.25;
const UNNAMED_PARKING_ROAD_MIN_ALIGNMENT_COSINE = Math.cos(
  15 * Math.PI / 180,
);
const UNNAMED_PARKING_ROAD_AMBIGUITY_M = 1.25;

export function createUrbanStructureBatch(
  tileId: WorldTileId,
  structures: readonly Readonly<RuntimeLinearFeature>[],
  hardscape: readonly Readonly<RuntimeAreaFeature>[],
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  materials: CityMaterialLibrary,
  roads: readonly Readonly<RuntimeLinearFeature>[] = [],
  bridgeCollisions: readonly Readonly<AuthoredBridgeCollisionSpec>[] = [],
  tileOrigin: Readonly<{ x: number; z: number }> = { x: 0, z: 0 },
  buildings: readonly Readonly<BuildingFootprintData>[] = [],
  trees: readonly Readonly<RuntimeTreeInstance>[] = [],
  renderMappedParkedVehicles = true,
): UrbanStructureBatch | null {
  if (structures.length === 0 && hardscape.length === 0) {
    return null;
  }
  const object = new Group();
  object.name = `runtime-urban-structures:${tileId}`;
  object.userData = {
    tileId,
    sourceLayer: "osm-urban-structures",
    representation:
      "source-positioned-surface-following-hardscape-parcel-seams-stairs-barriers-and-retaining-edges",
  };

  const hardscapeGroups: readonly {
    key: HardscapeMaterialGroup;
    material: MeshStandardMaterial;
  }[] = [
    {
      key: "pedestrian",
      material: materials.streetSurfaces[family].pedestrian,
    },
    {
      key: "carriageway",
      material: materials.streetSurfaces[family].carriageway,
    },
    { key: "unpaved", material: materials.terrain[0] },
    { key: "marking", material: materials.roadMarking },
  ];
  const renderableHardscape = hardscape.filter(
    (feature) =>
      !VILNIUS_CIVIC_SQUARE_REPLACED_HARDSCAPE_IDS.has(feature.id) &&
      feature.classCode !==
        "official-orthophoto:parking-space:detected-vehicle",
  );
  for (const group of hardscapeGroups) {
    addMesh(
      object,
      createHardscapeGeometry(
        renderableHardscape.filter((feature) =>
          hardscapeMaterialGroup(feature.classCode) === group.key
        ),
        terrain,
      ),
      group.material,
      `${object.name}:hardscape-${group.key}`,
      true,
    );
  }

  const renderableStructures = structures.filter(
    (feature) => !isSuppressedAuthoredGenericStructureId(feature.id),
  );
  const steps = renderableStructures.filter((feature) =>
    feature.classCode === "steps"
  );
  addMesh(
    object,
    createStepGeometry(steps, terrain),
    materials.streetSurfaces[family].sidewalk,
    `${object.name}:steps`,
    true,
  );

  // Runtime tiles already carry validated parked-vehicle descriptors and
  // render them through the streamed field/interactive-vehicle pipeline.
  // Regenerating their offline placement analysis here blocks tile attach.
  const parkedVehicles: ParkedVehicleGeometry = renderMappedParkedVehicles
    ? createMappedParkedVehicleGeometry(
      tileId,
      hardscape,
      renderableStructures.filter((feature) =>
        feature.classCode.startsWith("parking_lane:") ||
        feature.classCode.startsWith("orthophoto_parking_row:")
      ),
      renderableStructures.filter((feature) =>
        PARKING_PHYSICAL_BARRIER_CLASSES.has(feature.classCode)
      ),
      roads,
      terrain,
      buildings,
      trees,
      true,
    )
    : {
      body: null,
      glass: null,
      wheels: null,
      count: 0,
      roadsideCount: 0,
      placements: [],
    };
  addMesh(
    object,
    parkedVehicles.body,
    materials.parkedVehicleBody,
    `${object.name}:mapped-parked-vehicle-bodies`,
    false,
  );
  addMesh(
    object,
    parkedVehicles.glass,
    materials.window,
    `${object.name}:mapped-parked-vehicle-glass`,
    false,
  );
  addMesh(
    object,
    parkedVehicles.wheels,
    materials.streetPole,
    `${object.name}:mapped-parked-vehicle-wheels`,
    false,
  );

  const fences = renderableStructures.filter((feature) =>
    feature.classCode === "fence"
  );
  addMesh(
    object,
    createFenceGeometry(fences, terrain),
    materials.streetPole,
    `${object.name}:fences`,
    false,
  );

  const hedges = renderableStructures.filter((feature) =>
    feature.classCode === "hedge"
  );
  addMesh(
    object,
    createHedgeGeometry(hedges, terrain),
    materials.hedge,
    `${object.name}:hedges`,
    false,
  );

  const trolleyWires = renderableStructures.filter((feature) =>
    feature.classCode === "trolley_wire"
  );
  addMesh(
    object,
    createTrolleyWireGeometry(
      trolleyWires,
      terrain,
      bridgeCollisions,
      tileOrigin,
    ),
    materials.streetPole,
    `${object.name}:trolley-wires`,
    false,
  );

  const parcelBoundaries = renderableStructures.filter((feature) =>
    feature.classCode === "parcel_boundary"
  );
  addMesh(
    object,
    createParcelBoundaryGeometry(parcelBoundaries, terrain),
    materials.parcelBoundary,
    `${object.name}:official-parcel-boundaries`,
    true,
  );

  const masonry = renderableStructures.filter((feature) =>
    feature.classCode !== "steps" &&
    feature.classCode !== "fence" &&
    feature.classCode !== "hedge" &&
    feature.classCode !== "trolley_wire" &&
    feature.classCode !== "parcel_boundary" &&
    !feature.classCode.startsWith("parking_lane:")
  );
  addMesh(
    object,
    createBarrierGeometry(masonry, terrain),
    materials.streetSurfaces[family].curb,
    `${object.name}:walls-and-retaining-edges`,
    true,
  );

  if (object.children.length === 0) {
    return null;
  }
  return {
    object,
    structureCount: structures.length,
    hardscapePolygonCount: renderableHardscape.reduce(
      (count, feature) => count + feature.polygons.length,
      0,
    ),
    mappedParkedVehicleCount: parkedVehicles.count,
    mappedRoadsideParkedVehicleCount: parkedVehicles.roadsideCount,
  };
}

interface ParkedVehicleGeometry {
  readonly body: BufferGeometry | null;
  readonly glass: BufferGeometry | null;
  readonly wheels: BufferGeometry | null;
  readonly count: number;
  readonly roadsideCount: number;
  readonly placements: readonly MappedParkedVehiclePlacement[];
}

interface ParkedVehicleBuffers {
  readonly bodyPositions: number[];
  readonly bodyIndices: number[];
  readonly bodyColours: number[];
  readonly glassPositions: number[];
  readonly glassIndices: number[];
  readonly wheelPositions: number[];
  readonly wheelIndices: number[];
}

const PARKED_VEHICLE_COLOURS = Object.freeze([
  [0.23, 0.27, 0.29],
  [0.54, 0.53, 0.48],
  [0.43, 0.18, 0.14],
  [0.2, 0.27, 0.22],
  [0.67, 0.65, 0.58],
] as const);
const PARKED_VEHICLE_LENGTH_M = 4.45;
const PARKED_VEHICLE_WIDTH_M = 1.78;
const PARKED_VEHICLE_CLEARANCE_LENGTH_M = Math.max(
  ...Object.values(MAPPED_ROAD_VEHICLE_FAMILY_PROFILES).map(
    (profile) => profile.bodySizeM.z,
  ),
);
const PARKED_VEHICLE_CLEARANCE_WIDTH_M = Math.max(
  ...Object.values(MAPPED_ROAD_VEHICLE_FAMILY_PROFILES).map(
    (profile) => profile.bodySizeM.x,
  ),
);
const PARKED_VEHICLE_MAXIMUM_BODY_HEIGHT_M = Math.max(
  ...Object.values(MAPPED_ROAD_VEHICLE_FAMILY_PROFILES).map(
    (profile) => profile.bodySizeM.y,
  ),
);
const PARKED_VEHICLE_END_MARGIN_M = 2.65;
const PARKED_VEHICLE_SLOT_M = 6.35;
const PARKED_VEHICLE_EDGE_OFFSET_M = 1.34;
const MAX_PARKED_VEHICLES_PER_FEATURE_PER_TILE = 14;
const ROADSIDE_PARKING_END_MARGIN_M = 5.5;
const MAX_ROADSIDE_PARKED_VEHICLES_PER_SIDE_PER_TILE = 16;
const PARKING_PHYSICAL_BARRIER_CLASSES = new Set([
  "fence",
  "hedge",
  "wall",
  "retaining_wall",
]);
const PARKED_VEHICLE_FAMILIES: readonly RuntimeParkedVehicleFamily[] =
  Object.freeze([
    "compact-hatch",
    "estate-wagon",
    "ordinary-sedan",
    "small-crossover",
    "compact-van",
    "older-boxy-car",
  ]);

function createMappedParkedVehicleGeometry(
  tileId: WorldTileId,
  features: readonly Readonly<RuntimeAreaFeature>[],
  parkingLanes: readonly Readonly<RuntimeLinearFeature>[],
  barriers: readonly Readonly<RuntimeLinearFeature>[],
  roads: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  buildings: readonly Readonly<BuildingFootprintData>[],
  trees: readonly Readonly<RuntimeTreeInstance>[],
  buildGeometry = true,
): ParkedVehicleGeometry {
  const bodyPositions: number[] = [];
  const bodyIndices: number[] = [];
  const bodyColours: number[] = [];
  const glassPositions: number[] = [];
  const glassIndices: number[] = [];
  const wheelPositions: number[] = [];
  const wheelIndices: number[] = [];
  const buffers: ParkedVehicleBuffers = {
    bodyPositions,
    bodyIndices,
    bodyColours,
    glassPositions,
    glassIndices,
    wheelPositions,
    wheelIndices,
  };
  let count = 0;
  let roadsideCount = 0;
  const acceptedVehicles: VehiclePose[] = [];
  const placements: MappedParkedVehiclePlacement[] = [];
  const mappedParkingAreas = features.filter((feature) =>
    feature.classCode.toLowerCase().includes(":parking:") ||
    feature.classCode.toLowerCase().includes(":parking-space:")
  ).sort((left, right) =>
    parkingAreaPlacementPriority(right) - parkingAreaPlacementPriority(left) ||
    left.id.localeCompare(right.id)
  );
  const officialMunicipalParkingAreas = mappedParkingAreas.filter((feature) =>
    feature.classCode === "official-municipal:parking:inventory"
  );
  const buildingObstacles = parkingBuildingObstacles(buildings);
  const authoredCollisionObstacles = parkingAuthoredCollisionObstacles(
    tileId,
    buildings,
  );
  const roadVehicleObstacles = parkingRoadVehicleObstacles(tileId);
  const parkingSurfaceHeight = createParkingSurfaceHeightSampler(
    tileId,
    roads,
    terrain,
  );

  for (const feature of mappedParkingAreas) {
    const officialMunicipalInventory =
      feature.classCode === "official-municipal:parking:inventory";
    const officialOrthophotoDetectedVehicle =
      feature.classCode ===
        "official-orthophoto:parking-space:detected-vehicle";
    const centredOfficialSingleSpace = officialMunicipalInventory &&
      feature.parking?.capacity === 1;
    const explicitSpace = feature.parking?.placement === "parking_space" ||
      feature.classCode.includes("parking-space");
    const edge = officialOrthophotoDetectedVehicle
      ? detectedVehicleLongAxis(feature)
      : longestExteriorParkingEdge(
        feature,
        explicitSpace || centredOfficialSingleSpace
          ? 2
          : PARKED_VEHICLE_LENGTH_M + 1,
      );
    if (edge === null) {
      continue;
    }
    const lengthM = Math.hypot(edge.to.x - edge.from.x, edge.to.z - edge.from.z);
    const orientation = feature.parking?.orientation ?? "parallel";
    const slotSpacingM = parkingSlotSpacingM(orientation);
    const geometrySlotCount = explicitSpace || centredOfficialSingleSpace
      ? 1
      : Math.min(
      MAX_PARKED_VEHICLES_PER_FEATURE_PER_TILE,
      Math.floor((lengthM - PARKED_VEHICLE_END_MARGIN_M * 2) /
        slotSpacingM) + 1,
      );
    const slotCount = officialMunicipalInventory
      ? Math.min(feature.parking?.capacity ?? 0, geometrySlotCount)
      : geometrySlotCount;
    if (slotCount <= 0) {
      continue;
    }
    const edgeDirection = {
      x: (edge.to.x - edge.from.x) / lengthM,
      z: (edge.to.z - edge.from.z) / lengthM,
    };
    const direction = parkingDirectionForOrientation(
      edgeDirection,
      orientation,
      1,
      feature.id,
    );
    const normals = [
      { x: -edgeDirection.z, z: edgeDirection.x },
      { x: edgeDirection.z, z: -edgeDirection.x },
    ] as const;
    const usableLengthM = Math.max(
      0,
      lengthM - PARKED_VEHICLE_END_MARGIN_M * 2,
    );
    for (let slot = 0; slot < slotCount; slot += 1) {
      const occupancy = officialOrthophotoDetectedVehicle
        ? 1
        : explicitSpace
        ? 0.78
        : 0.58;
      if (hashUnit(`${feature.id}:${slot}:occupied`) >= occupancy) {
        continue;
      }
      const distanceM = slotCount === 1
        ? lengthM / 2
        : PARKED_VEHICLE_END_MARGIN_M + usableLengthM * slot /
          (slotCount - 1);
      const edgePoint = {
        x: edge.from.x + edgeDirection.x * distanceM,
        z: edge.from.z + edgeDirection.z * distanceM,
      };
      const inwardOffsetM = orientation === "parallel"
        ? PARKED_VEHICLE_EDGE_OFFSET_M
        : orientation === "diagonal"
        ? 1.95
        : PARKED_VEHICLE_LENGTH_M / 2 + 0.24;
      const edgeCandidates = normals.map((normal) => ({
        x: edgePoint.x + normal.x * inwardOffsetM,
        z: edgePoint.z + normal.z * inwardOffsetM,
      })).find((candidate) =>
        parkedVehicleFitsFeature(candidate, direction, feature)
      );
      const centre = officialOrthophotoDetectedVehicle
        ? detectedVehicleCentre(feature)
        : explicitSpace || centredOfficialSingleSpace
        ? areaFeatureCentroid(feature, direction) ?? edgeCandidates
        : edgeCandidates;
      if (centre === undefined) {
        continue;
      }
      if (
        !officialMunicipalInventory &&
        !officialOrthophotoDetectedVehicle &&
        officialMunicipalParkingAreas.some((area) =>
          pointInAreaFeature(centre, area)
        )
      ) {
        continue;
      }
      if (parkedVehicleIntersectsBuildings(
        centre,
        direction,
        buildingObstacles,
      )) {
        continue;
      }
      if (parkedVehicleIntersectsAuthoredCollisions(
        centre,
        direction,
        terrain,
        authoredCollisionObstacles,
      )) {
        continue;
      }
      if (parkedVehicleIntersectsRoadVehicles(
        centre,
        direction,
        terrain,
        roadVehicleObstacles,
      )) {
        continue;
      }
      if (parkedVehicleIntersectsTrees(centre, direction, trees)) {
        continue;
      }
      if (parkedVehicleIntersectsBarriers(centre, direction, barriers)) {
        continue;
      }
      if (acceptedVehicles.some((vehicle) => parkedVehiclesOverlap(
        { centre, direction },
        vehicle,
      ))) {
        continue;
      }
      const stableKey = `${feature.id}:${orientation}:${slot}`;
      const colour = PARKED_VEHICLE_COLOURS[
        Math.floor(hashUnit(`${feature.id}:${slot}:colour`) *
          PARKED_VEHICLE_COLOURS.length)
      ] ?? PARKED_VEHICLE_COLOURS[0];
      const placement = mappedParkedVehiclePlacement(
        tileId,
        stableKey,
        feature.sourceId,
        feature.id,
        officialMunicipalInventory
          ? "official-municipal-parking-inventory"
          : officialOrthophotoDetectedVehicle
          ? "official-orthophoto-detected-vehicle"
          : explicitSpace
          ? "osm-explicit-parking-space"
          : "osm-mapped-parking-area",
        centre,
        direction,
        terrain,
        colour,
        orientation,
        officialOrthophotoDetectedVehicle,
        parkingSurfaceHeight,
      );
      if (Math.abs(Math.tan(placement.pitchRadians)) > 0.2) {
        continue;
      }
      if (!mappedParkedVehicleHasEscapeClearance(
        placement,
        terrain,
        buildingObstacles,
        parkingSurfaceHeight,
      )) {
        continue;
      }
      placements.push(placement);
      if (buildGeometry) {
        appendParkedVehicle(
          buffers,
          centre,
          direction,
          terrain,
          colour,
          parkingSurfaceHeight,
        );
      }
      acceptedVehicles.push({ centre, direction });
      count += 1;
      roadsideCount += officialOrthophotoDetectedVehicle ? 1 : 0;
    }
  }

  for (const feature of parkingLanes) {
    if (feature.classCode.startsWith("orthophoto_parking_row:")) {
      const orientation = feature.parking?.orientation ?? "parallel";
      const roadside = feature.parking?.placement !== "parking_area";
      let featureCount = 0;
      for (let segment = 1; segment < feature.points.length; segment += 1) {
        const from = feature.points[segment - 1];
        const to = feature.points[segment];
        if (from === undefined || to === undefined) continue;
        const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
        if (lengthM < 1) continue;
        const lineDirection = {
          x: (to.x - from.x) / lengthM,
          z: (to.z - from.z) / lengthM,
        };
        const observedCount = feature.parking?.capacity ?? Math.max(
          1,
          Math.floor(lengthM / parkingSlotSpacingM(orientation)),
        );
        const slotCount = Math.min(
          MAX_ROADSIDE_PARKED_VEHICLES_PER_SIDE_PER_TILE - featureCount,
          observedCount,
        );
        for (let slot = 0; slot < slotCount; slot += 1) {
          const stableKey = `${feature.id}:reviewed:${segment}:${slot}`;
          const direction = parkingDirectionForOrientation(
            lineDirection,
            orientation,
            1,
            stableKey,
          );
          const amount = slotCount === 1 ? 0.5 : slot / (slotCount - 1);
          const centre = {
            x: from.x + (to.x - from.x) * amount,
            z: from.z + (to.z - from.z) * amount,
          };
          if (
            !parkedVehicleFitsTile(centre, direction) ||
            parkedVehicleIntersectsBuildings(
              centre,
              direction,
              buildingObstacles,
            ) ||
            parkedVehicleIntersectsAuthoredCollisions(
              centre,
              direction,
              terrain,
              authoredCollisionObstacles,
            ) ||
            parkedVehicleIntersectsRoadVehicles(
              centre,
              direction,
              terrain,
              roadVehicleObstacles,
            ) ||
            parkedVehicleIntersectsTrees(centre, direction, trees) ||
            parkedVehicleIntersectsBarriers(centre, direction, barriers) ||
            acceptedVehicles.some((vehicle) => parkedVehiclesOverlap(
              { centre, direction },
              vehicle,
            ))
          ) {
            continue;
          }
          const colour = PARKED_VEHICLE_COLOURS[
            Math.floor(hashUnit(`${stableKey}:colour`) *
              PARKED_VEHICLE_COLOURS.length)
          ] ?? PARKED_VEHICLE_COLOURS[0];
          const vehiclePlacement = mappedParkedVehiclePlacement(
            tileId,
            stableKey,
            feature.sourceId,
            feature.id,
            "official-orthophoto-reviewed-row",
            centre,
            direction,
            terrain,
            colour,
            orientation,
            roadside,
            parkingSurfaceHeight,
          );
          if (!mappedParkedVehicleHasEscapeClearance(
            vehiclePlacement,
            terrain,
            buildingObstacles,
            parkingSurfaceHeight,
          )) {
            continue;
          }
          placements.push(vehiclePlacement);
          if (buildGeometry) {
            appendParkedVehicle(
              buffers,
              centre,
              direction,
              terrain,
              colour,
              parkingSurfaceHeight,
            );
          }
          acceptedVehicles.push({ centre, direction });
          featureCount += 1;
          count += 1;
          if (roadside) {
            roadsideCount += 1;
          }
        }
      }
      continue;
    }
    const match = feature.classCode.match(
      /^parking_lane:(left|right|both):(parallel|diagonal|perpendicular)$/u,
    );
    if (match === null) {
      continue;
    }
    const orientation = feature.parking?.orientation ?? match[2] as
      RuntimeParkingOrientation;
    const parkingPlacement = feature.parking?.placement ?? "lane";
    const sides = match[1] === "both"
      ? ["left", "right"] as const
      : [match[1] as "left" | "right"];
    for (const side of sides) {
      let sideCount = 0;
      for (let segment = 1; segment < feature.points.length; segment += 1) {
        const from = feature.points[segment - 1];
        const to = feature.points[segment];
        if (from === undefined || to === undefined) {
          continue;
        }
        const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
        const road = matchedOfficialParkingRoadAtPoint(
          feature,
          { x: (from.x + to.x) / 2, z: (from.z + to.z) / 2 },
          roads,
        );
        if (road === null) {
          continue;
        }
        const carriagewayWidthM = streetFeatureWidthM(road);
        if (
          match[1] === "both" && parkingPlacement === "lane" &&
          carriagewayWidthM < 6.5
        ) {
          continue;
        }
        const crossHalfExtentM = orientation === "parallel"
          ? PARKED_VEHICLE_WIDTH_M / 2
          : orientation === "diagonal"
          ? (PARKED_VEHICLE_LENGTH_M + PARKED_VEHICLE_WIDTH_M) /
            (2 * Math.SQRT2)
          : PARKED_VEHICLE_LENGTH_M / 2;
        const roadEnvelopeWidthM = road.totalWidthM ?? carriagewayWidthM;
        const isOutsideCarriageway = parkingPlacement === "street_side" ||
          parkingPlacement === "shoulder";
        const lateralOffsetM = isOutsideCarriageway
          ? roadEnvelopeWidthM / 2 + crossHalfExtentM + 0.18
          : parkingPlacement === "on_kerb"
          ? carriagewayWidthM / 2 + crossHalfExtentM * 0.5
          : parkingPlacement === "half_on_kerb"
          ? carriagewayWidthM / 2
          : carriagewayWidthM / 2 - crossHalfExtentM - 0.18;
        if (
          !isOutsideCarriageway &&
          lateralOffsetM < crossHalfExtentM * 0.35
        ) {
          continue;
        }
        const remaining = MAX_ROADSIDE_PARKED_VEHICLES_PER_SIDE_PER_TILE -
          sideCount;
        const slotCount = Math.min(
          remaining,
          Math.floor((lengthM - ROADSIDE_PARKING_END_MARGIN_M * 2) /
            parkingSlotSpacingM(orientation)) + 1,
        );
        if (slotCount <= 0) {
          continue;
        }
        const roadDirection = {
          x: (to.x - from.x) / lengthM,
          z: (to.z - from.z) / lengthM,
        };
        // Local +Z points south, so the map-left normal is the opposite of
        // the usual X/Y planar normal.
        const leftNormal = { x: roadDirection.z, z: -roadDirection.x };
        const sideFactor = side === "left" ? 1 : -1;
        const usableLengthM = lengthM - ROADSIDE_PARKING_END_MARGIN_M * 2;
        for (let slot = 0; slot < slotCount; slot += 1) {
          const stableKey = `${feature.id}:${side}:${segment}:${slot}`;
          if (hashUnit(`${stableKey}:occupied`) >= 0.58) {
            continue;
          }
          const direction = parkingDirectionForOrientation(
            roadDirection,
            orientation,
            sideFactor,
            stableKey,
          );
          const distanceM = ROADSIDE_PARKING_END_MARGIN_M +
            (slotCount === 1 ? usableLengthM / 2 : usableLengthM * slot /
              (slotCount - 1));
          const centre = {
            x: from.x + roadDirection.x * distanceM +
              leftNormal.x * lateralOffsetM * sideFactor,
            z: from.z + roadDirection.z * distanceM +
              leftNormal.z * lateralOffsetM * sideFactor,
          };
          if (!parkedVehicleFitsTile(centre, direction)) {
            continue;
          }
          const maximumRoadEdgeDistanceM = isOutsideCarriageway
            ? roadEnvelopeWidthM / 2 + crossHalfExtentM * 2 + 0.18
            : parkingPlacement === "on_kerb"
            ? carriagewayWidthM / 2 + crossHalfExtentM * 1.5
            : parkingPlacement === "half_on_kerb"
            ? carriagewayWidthM / 2 + crossHalfExtentM
            : carriagewayWidthM / 2 - 0.05;
          if (!parkedVehicleFitsRoadEnvelope(
            centre,
            direction,
            road,
            maximumRoadEdgeDistanceM + 0.45,
          )) {
            continue;
          }
          if (
            !isOutsideCarriageway && parkingPlacement !== "on_kerb" &&
            parkingPlacement !== "half_on_kerb" &&
            !parkedVehicleFitsRoad(
              centre,
              direction,
              road,
              carriagewayWidthM,
            )
          ) {
            continue;
          }
          if (mappedParkingAreas.some((area) => pointInAreaFeature(centre, area))) {
            continue;
          }
          if (parkedVehicleIntersectsBuildings(
            centre,
            direction,
            buildingObstacles,
          )) {
            continue;
          }
          if (parkedVehicleIntersectsAuthoredCollisions(
            centre,
            direction,
            terrain,
            authoredCollisionObstacles,
          )) {
            continue;
          }
          if (parkedVehicleIntersectsRoadVehicles(
            centre,
            direction,
            terrain,
            roadVehicleObstacles,
          )) {
            continue;
          }
          if (parkedVehicleIntersectsTrees(centre, direction, trees)) {
            continue;
          }
          if (parkedVehicleIntersectsBarriers(centre, direction, barriers)) {
            continue;
          }
          if (acceptedVehicles.some((vehicle) => parkedVehiclesOverlap(
            { centre, direction },
            vehicle,
          ))) {
            continue;
          }
          const colour = PARKED_VEHICLE_COLOURS[
            Math.floor(hashUnit(`${stableKey}:colour`) *
              PARKED_VEHICLE_COLOURS.length)
          ] ?? PARKED_VEHICLE_COLOURS[0];
          const placement = mappedParkedVehiclePlacement(
            tileId,
            stableKey,
            feature.sourceId,
            feature.id,
            "osm-explicit-street-parking",
            centre,
            direction,
            terrain,
            colour,
            orientation,
            true,
            parkingSurfaceHeight,
          );
          if (!mappedParkedVehicleHasEscapeClearance(
            placement,
            terrain,
            buildingObstacles,
            parkingSurfaceHeight,
          )) {
            continue;
          }
          placements.push(placement);
          if (buildGeometry) {
            appendParkedVehicle(
              buffers,
              centre,
              direction,
              terrain,
              colour,
              parkingSurfaceHeight,
            );
          }
          acceptedVehicles.push({ centre, direction });
          count += 1;
          roadsideCount += 1;
          sideCount += 1;
        }
        if (
          sideCount >= MAX_ROADSIDE_PARKED_VEHICLES_PER_SIDE_PER_TILE
        ) {
          break;
        }
      }
    }
  }

  return {
    body: geometryFrom(bodyPositions, bodyIndices, bodyColours),
    glass: geometryFrom(glassPositions, glassIndices),
    wheels: geometryFrom(wheelPositions, wheelIndices),
    count,
    roadsideCount,
    placements,
  };
}

function parkingAreaPlacementPriority(
  feature: Readonly<RuntimeAreaFeature>,
): number {
  if (
    feature.classCode ===
      "official-orthophoto:parking-space:detected-vehicle"
  ) return 4;
  if (feature.classCode === "official-municipal:parking:inventory") return 3;
  if (
    feature.parking?.placement === "parking_space" ||
    feature.classCode.includes("parking-space")
  ) return 2;
  return 1;
}

function detectedVehicleLongAxis(
  feature: Readonly<RuntimeAreaFeature>,
): Readonly<{
  from: Readonly<{ x: number; z: number }>;
  to: Readonly<{ x: number; z: number }>;
}> | null {
  const points = uniqueAreaFeaturePoints(feature);
  if (points.length < 3) return null;
  const centre = meanPoint(points);
  let xx = 0;
  let xz = 0;
  let zz = 0;
  for (const point of points) {
    const dx = point.x - centre.x;
    const dz = point.z - centre.z;
    xx += dx * dx;
    xz += dx * dz;
    zz += dz * dz;
  }
  const angle = Math.atan2(2 * xz, xx - zz) / 2;
  const direction = { x: Math.cos(angle), z: Math.sin(angle) };
  const projections = points.map((point) =>
    (point.x - centre.x) * direction.x +
    (point.z - centre.z) * direction.z
  );
  const minimum = Math.min(...projections);
  const maximum = Math.max(...projections);
  if (maximum - minimum < 2) return null;
  return {
    from: {
      x: centre.x + direction.x * minimum,
      z: centre.z + direction.z * minimum,
    },
    to: {
      x: centre.x + direction.x * maximum,
      z: centre.z + direction.z * maximum,
    },
  };
}

function detectedVehicleCentre(
  feature: Readonly<RuntimeAreaFeature>,
): Readonly<{ x: number; z: number }> | undefined {
  const points = uniqueAreaFeaturePoints(feature);
  return points.length < 3 ? undefined : meanPoint(points);
}

function uniqueAreaFeaturePoints(
  feature: Readonly<RuntimeAreaFeature>,
): readonly Readonly<{ x: number; z: number }>[] {
  const points = new Map<string, Readonly<{ x: number; z: number }>>();
  for (const polygon of feature.polygons) {
    for (const point of stripClosingPoint(polygon.outer)) {
      points.set(`${point.x.toFixed(3)}:${point.z.toFixed(3)}`, point);
    }
  }
  return [...points.values()];
}

function meanPoint(
  points: readonly Readonly<{ x: number; z: number }>[],
): Readonly<{ x: number; z: number }> {
  const sum = points.reduce((value, point) => ({
    x: value.x + point.x,
    z: value.z + point.z,
  }), { x: 0, z: 0 });
  return { x: sum.x / points.length, z: sum.z / points.length };
}

function parkingSlotSpacingM(orientation: RuntimeParkingOrientation): number {
  return orientation === "parallel"
    ? PARKED_VEHICLE_SLOT_M
    : orientation === "diagonal"
    ? 3.75
    : 2.78;
}

function parkingDirectionForOrientation(
  lineDirection: Readonly<{ x: number; z: number }>,
  orientation: RuntimeParkingOrientation,
  sideFactor: number,
  stableKey: string,
): Readonly<{ x: number; z: number }> {
  if (orientation === "parallel") return lineDirection;
  const outward = {
    x: lineDirection.z * sideFactor,
    z: -lineDirection.x * sideFactor,
  };
  const direction = orientation === "perpendicular"
    ? outward
    : {
      x: lineDirection.x * Math.SQRT1_2 + outward.x * Math.SQRT1_2,
      z: lineDirection.z * Math.SQRT1_2 + outward.z * Math.SQRT1_2,
    };
  const facingFactor = hashUnit(`${stableKey}:facing`) < 0.5 ? 1 : -1;
  return {
    x: direction.x * facingFactor,
    z: direction.z * facingFactor,
  };
}

function areaFeatureCentroid(
  feature: Readonly<RuntimeAreaFeature>,
  direction: Readonly<{ x: number; z: number }>,
): Readonly<{ x: number; z: number }> | undefined {
  for (const polygon of feature.polygons) {
    const ring = stripClosingPoint(polygon.outer);
    if (ring.length === 0) continue;
    const centre = ring.reduce(
      (sum, point) => ({ x: sum.x + point.x, z: sum.z + point.z }),
      { x: 0, z: 0 },
    );
    const candidate = {
      x: centre.x / ring.length,
      z: centre.z / ring.length,
    };
    if (parkedVehicleFitsFeature(candidate, direction, feature)) {
      return candidate;
    }
  }
  return undefined;
}

export function mappedParkedVehiclePlacements(
  tileId: WorldTileId,
  features: readonly Readonly<RuntimeAreaFeature>[],
  structures: readonly Readonly<RuntimeLinearFeature>[],
  roads: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  buildings: readonly Readonly<BuildingFootprintData>[],
  trees: readonly Readonly<RuntimeTreeInstance>[],
): readonly MappedParkedVehiclePlacement[] {
  const parkingLanes = structures.filter((feature) =>
    feature.classCode.startsWith("parking_lane:") ||
    feature.classCode.startsWith("orthophoto_parking_row:")
  );
  const barriers = structures.filter((feature) =>
    PARKING_PHYSICAL_BARRIER_CLASSES.has(feature.classCode)
  );
  return createMappedParkedVehicleGeometry(
    tileId,
    features,
    parkingLanes,
    barriers,
    roads,
    terrain,
    buildings,
    trees,
    false,
  ).placements;
}

function mappedParkedVehiclePlacement(
  tileId: WorldTileId,
  stableKey: string,
  sourceId: string,
  sourceFeatureId: string,
  provenance: RuntimeParkedVehicleDescriptor["provenance"],
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  terrain: Readonly<TerrainTileData>,
  colour: readonly [number, number, number],
  orientation: RuntimeParkingOrientation,
  roadside: boolean,
  parkingSurfaceHeight?: ParkingSurfaceHeightSampler,
): MappedParkedVehiclePlacement {
  const family = parkedVehicleFamilyForStableKey(stableKey);
  const bodyLengthM = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[family].bodySizeM.z;
  const halfLength = bodyLengthM / 2;
  const rearGroundY = sampleParkingGroundHeight(
    terrain,
    centre.x - direction.x * halfLength,
    centre.z - direction.z * halfLength,
    parkingSurfaceHeight,
  );
  const frontGroundY = sampleParkingGroundHeight(
    terrain,
    centre.x + direction.x * halfLength,
    centre.z + direction.z * halfLength,
    parkingSurfaceHeight,
  );
  return {
    id: `mapped-parked:${tileId}:${hashInteger(stableKey).toString(36)}`,
    tileId,
    sourceId,
    sourceFeatureId,
    provenance,
    centre: { ...centre },
    direction: { ...direction },
    groundY: (rearGroundY + frontGroundY) / 2,
    pitchRadians: -Math.atan2(
      frontGroundY - rearGroundY,
      bodyLengthM,
    ),
    bodyColorHex:
      (Math.round(colour[0] * 255) << 16) |
      (Math.round(colour[1] * 255) << 8) |
      Math.round(colour[2] * 255),
    family,
    orientation,
    roadside,
  };
}

function parkedVehicleFamilyForStableKey(
  stableKey: string,
): RuntimeParkedVehicleFamily {
  return PARKED_VEHICLE_FAMILIES[
    Math.floor(hashUnit(`${stableKey}:family`) *
      PARKED_VEHICLE_FAMILIES.length)
  ] ?? "ordinary-sedan";
}

type ParkingSurfaceHeightSampler = (x: number, z: number) =>
  number | undefined;

interface ParkingSurfaceTriangle {
  readonly minimumX: number;
  readonly minimumZ: number;
  readonly maximumX: number;
  readonly maximumZ: number;
  readonly first: Readonly<{ x: number; y: number; z: number }>;
  readonly second: Readonly<{ x: number; y: number; z: number }>;
  readonly third: Readonly<{ x: number; y: number; z: number }>;
  readonly denominator: number;
}

const PARKING_SURFACE_QUERY_CELL_M = 8;

function sampleParkingGroundHeight(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
  parkingSurfaceHeight?: ParkingSurfaceHeightSampler,
): number {
  return Math.max(
    sampleHeight(terrain, x, z),
    parkingSurfaceHeight?.(x, z) ?? Number.NEGATIVE_INFINITY,
  ) + 0.055;
}

function createParkingSurfaceHeightSampler(
  tileId: WorldTileId,
  roads: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): ParkingSurfaceHeightSampler | undefined {
  if (roads.length === 0) return undefined;
  const [tileX, tileZ] = tileId.split(":").map(Number);
  if (!Number.isFinite(tileX) || !Number.isFinite(tileZ)) {
    throw new Error(`Invalid world tile ID ${tileId}`);
  }
  const tileOrigin = { x: tileX! * 256, z: tileZ! * 256 };
  const mesh = createStreetWalkableColliderMesh(
    roads,
    terrain,
    streetSurfaceFamilyForTile(tileOrigin, 256),
    { tileOrigin },
  );
  if (mesh === null) return undefined;
  const trianglesByCell = new Map<string, ParkingSurfaceTriangle[]>();
  for (let index = 0; index < mesh.indices.length; index += 3) {
    const firstIndex = mesh.indices[index];
    const secondIndex = mesh.indices[index + 1];
    const thirdIndex = mesh.indices[index + 2];
    if (
      firstIndex === undefined || secondIndex === undefined ||
      thirdIndex === undefined
    ) {
      continue;
    }
    const first = {
      x: mesh.vertices[firstIndex * 3]!,
      y: mesh.vertices[firstIndex * 3 + 1]!,
      z: mesh.vertices[firstIndex * 3 + 2]!,
    };
    const second = {
      x: mesh.vertices[secondIndex * 3]!,
      y: mesh.vertices[secondIndex * 3 + 1]!,
      z: mesh.vertices[secondIndex * 3 + 2]!,
    };
    const third = {
      x: mesh.vertices[thirdIndex * 3]!,
      y: mesh.vertices[thirdIndex * 3 + 1]!,
      z: mesh.vertices[thirdIndex * 3 + 2]!,
    };
    const denominator =
      (second.z - third.z) * (first.x - third.x) +
      (third.x - second.x) * (first.z - third.z);
    if (Math.abs(denominator) <= 1e-7) continue;
    const triangle: ParkingSurfaceTriangle = {
      minimumX: Math.min(first.x, second.x, third.x),
      minimumZ: Math.min(first.z, second.z, third.z),
      maximumX: Math.max(first.x, second.x, third.x),
      maximumZ: Math.max(first.z, second.z, third.z),
      first,
      second,
      third,
      denominator,
    };
    const minimumCellX = Math.floor(
      triangle.minimumX / PARKING_SURFACE_QUERY_CELL_M,
    );
    const minimumCellZ = Math.floor(
      triangle.minimumZ / PARKING_SURFACE_QUERY_CELL_M,
    );
    const maximumCellX = Math.floor(
      triangle.maximumX / PARKING_SURFACE_QUERY_CELL_M,
    );
    const maximumCellZ = Math.floor(
      triangle.maximumZ / PARKING_SURFACE_QUERY_CELL_M,
    );
    for (let cellX = minimumCellX; cellX <= maximumCellX; cellX += 1) {
      for (let cellZ = minimumCellZ; cellZ <= maximumCellZ; cellZ += 1) {
        const key = `${cellX}:${cellZ}`;
        const values = trianglesByCell.get(key);
        if (values === undefined) {
          trianglesByCell.set(key, [triangle]);
        } else {
          values.push(triangle);
        }
      }
    }
  }
  return (x, z) => {
    let maximumY: number | undefined;
    const cellX = Math.floor(x / PARKING_SURFACE_QUERY_CELL_M);
    const cellZ = Math.floor(z / PARKING_SURFACE_QUERY_CELL_M);
    for (const triangle of trianglesByCell.get(`${cellX}:${cellZ}`) ?? []) {
      if (
        x < triangle.minimumX - 1e-6 ||
        x > triangle.maximumX + 1e-6 ||
        z < triangle.minimumZ - 1e-6 ||
        z > triangle.maximumZ + 1e-6
      ) {
        continue;
      }
      const { first, second, third, denominator } = triangle;
      const firstWeight = (
        (second.z - third.z) * (x - third.x) +
        (third.x - second.x) * (z - third.z)
      ) / denominator;
      const secondWeight = (
        (third.z - first.z) * (x - third.x) +
        (first.x - third.x) * (z - third.z)
      ) / denominator;
      const thirdWeight = 1 - firstWeight - secondWeight;
      if (
        firstWeight < -1e-6 || secondWeight < -1e-6 ||
        thirdWeight < -1e-6
      ) {
        continue;
      }
      const y = first.y * firstWeight + second.y * secondWeight +
        third.y * thirdWeight;
      maximumY = maximumY === undefined ? y : Math.max(maximumY, y);
    }
    return maximumY;
  };
}

function mappedParkedVehicleHasEscapeClearance(
  placement: Readonly<MappedParkedVehiclePlacement>,
  terrain: Readonly<TerrainTileData>,
  buildings: readonly Readonly<ParkingBuildingObstacle>[],
  parkingSurfaceHeight?: ParkingSurfaceHeightSampler,
): boolean {
  const size = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[placement.family].bodySizeM;
  const halfLengthM = size.z / 2;
  const halfWidthM = size.x / 2;
  const halfHeightM = size.y / 2;
  const lateral = {
    x: -placement.direction.z,
    z: placement.direction.x,
  };
  const longitudinalSamples: number[] = [];
  for (
    let value = -halfLengthM;
    value < halfLengthM;
    value += 0.15
  ) {
    longitudinalSamples.push(value);
  }
  longitudinalSamples.push(halfLengthM);
  const travelSamplesM = [0, 0.09, 0.18, 0.27, 0.36, 0.45, 0.54] as const;
  const crosswiseSamplesM = [
    -halfWidthM,
    -halfWidthM / 2,
    0,
    halfWidthM / 2,
    halfWidthM,
  ] as const;
  return ([-1, 1] as const).some((travelDirection) => {
    for (const travelM of travelSamplesM) {
      const centreShiftM = travelM * travelDirection;
      const movedCentre = {
        x: placement.centre.x + placement.direction.x * centreShiftM,
        z: placement.centre.z + placement.direction.z * centreShiftM,
      };
      if (parkedVehicleIntersectsBuildings(
        movedCentre,
        placement.direction,
        buildings,
      )) {
        return false;
      }
      for (const longitudinalM of longitudinalSamples) {
        const bottomY = placement.groundY +
          halfHeightM * (1 - Math.cos(placement.pitchRadians)) -
          longitudinalM * Math.sin(placement.pitchRadians) +
          ROAD_VEHICLE_TUNING.maximumCurbStepM;
        for (const crosswiseM of crosswiseSamplesM) {
          const x = placement.centre.x +
            placement.direction.x * (centreShiftM + longitudinalM) +
            lateral.x * crosswiseM;
          const z = placement.centre.z +
            placement.direction.z * (centreShiftM + longitudinalM) +
            lateral.z * crosswiseM;
          const supportY = Math.max(
            sampleHeight(terrain, x, z),
            parkingSurfaceHeight?.(x, z) ?? Number.NEGATIVE_INFINITY,
          );
          // Preserve a small separation from trimesh edges: a mathematical
          // tangent can still be reported as time-of-impact zero by Rapier.
          if (supportY > bottomY - 0.02) {
            return false;
          }
        }
      }
    }
    return true;
  });
}

function appendParkedVehicle(
  buffers: ParkedVehicleBuffers,
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  terrain: Readonly<TerrainTileData>,
  colour: readonly [number, number, number],
  parkingSurfaceHeight?: ParkingSurfaceHeightSampler,
): void {
  const halfLength = PARKED_VEHICLE_LENGTH_M / 2;
  const rearGroundY = sampleParkingGroundHeight(
    terrain,
    centre.x - direction.x * halfLength,
    centre.z - direction.z * halfLength,
    parkingSurfaceHeight,
  ) + 0.055;
  const frontGroundY = sampleParkingGroundHeight(
    terrain,
    centre.x + direction.x * halfLength,
    centre.z + direction.z * halfLength,
    parkingSurfaceHeight,
  ) + 0.055;
  const groundY = (rearGroundY + frontGroundY) / 2;
  const longitudinalGroundSlope =
    (frontGroundY - rearGroundY) / PARKED_VEHICLE_LENGTH_M;
  appendOrientedBox(
    buffers.bodyPositions,
    buffers.bodyIndices,
    centre,
    direction,
    PARKED_VEHICLE_WIDTH_M,
    PARKED_VEHICLE_LENGTH_M,
    groundY + 0.16,
    0.62,
    buffers.bodyColours,
    colour,
    longitudinalGroundSlope,
  );
  appendOrientedBox(
    buffers.bodyPositions,
    buffers.bodyIndices,
    centre,
    direction,
    1.48,
    1.82,
    groundY + 1.19,
    0.09,
    buffers.bodyColours,
    colour,
    longitudinalGroundSlope,
  );
  appendOrientedBox(
    buffers.glassPositions,
    buffers.glassIndices,
    centre,
    direction,
    1.52,
    2.18,
    groundY + 0.74,
    0.48,
    undefined,
    undefined,
    longitudinalGroundSlope,
  );
  for (const longitudinal of [-1.34, 1.34]) {
    for (const lateral of [-0.83, 0.83]) {
      const wheelCentre = {
        x: centre.x + direction.x * longitudinal - direction.z * lateral,
        z: centre.z + direction.z * longitudinal + direction.x * lateral,
      };
      appendOrientedBox(
        buffers.wheelPositions,
        buffers.wheelIndices,
        wheelCentre,
        direction,
        0.22,
        0.58,
        groundY + longitudinalGroundSlope * longitudinal,
        0.38,
        undefined,
        undefined,
        longitudinalGroundSlope,
      );
    }
  }
}

export function matchedOfficialParkingRoadAtPoint(
  feature: Readonly<RuntimeLinearFeature>,
  point: Readonly<{ x: number; z: number }>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
): Readonly<RuntimeLinearFeature> | null {
  const name = normalizedFeatureName(feature.name);
  if (name !== null) {
    return roads
      .filter((road) =>
        road.kind === "road" && normalizedFeatureName(road.name) === name
      )
      .map((road) => ({
        road,
        distanceM: distanceToPolyline(point, road.points),
      }))
      .filter(({ distanceM }) => distanceM <= 6.25)
      .sort((left, right) =>
        left.distanceM - right.distanceM ||
        left.road.id.localeCompare(right.road.id)
      )[0]?.road ?? null;
  }

  const featureSample = nearestPolylineSegment(point, feature.points);
  if (featureSample === null) {
    return null;
  }
  const candidates = roads
    .flatMap((road) => {
      if (road.kind !== "road") return [];
      const sample = nearestPolylineSegment(point, road.points);
      if (sample === null) return [];
      const alignment = Math.abs(
        featureSample.direction.x * sample.direction.x +
        featureSample.direction.z * sample.direction.z,
      );
      return sample.distanceM <= UNNAMED_PARKING_ROAD_MAX_DISTANCE_M &&
          alignment >= UNNAMED_PARKING_ROAD_MIN_ALIGNMENT_COSINE
        ? [{ road, distanceM: sample.distanceM }]
        : [];
    })
    .sort((left, right) =>
      left.distanceM - right.distanceM || left.road.id.localeCompare(right.road.id)
    );
  const best = candidates[0];
  const second = candidates[1];
  if (
    best === undefined ||
    (second !== undefined &&
      second.distanceM <= best.distanceM +
        UNNAMED_PARKING_ROAD_AMBIGUITY_M)
  ) {
    return null;
  }
  return best.road;
}

function normalizedFeatureName(value: string | undefined): string | null {
  const normalized = value?.trim().toLocaleLowerCase("lt-LT");
  return normalized === undefined || normalized.length === 0
    ? null
    : normalized;
}

function distanceToPolyline(
  point: Readonly<{ x: number; z: number }>,
  line: readonly Readonly<{ x: number; z: number }>[],
): number {
  let minimumM = Number.POSITIVE_INFINITY;
  for (let index = 1; index < line.length; index += 1) {
    const from = line[index - 1];
    const to = line[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
    const amount = lengthSquared <= Number.EPSILON
      ? 0
      : clamp(
        ((point.x - from.x) * deltaX + (point.z - from.z) * deltaZ) /
          lengthSquared,
        0,
        1,
      );
    minimumM = Math.min(
      minimumM,
      Math.hypot(
        point.x - (from.x + deltaX * amount),
        point.z - (from.z + deltaZ * amount),
      ),
    );
  }
  return minimumM;
}

function nearestPolylineSegment(
  point: Readonly<{ x: number; z: number }>,
  line: readonly Readonly<{ x: number; z: number }>[],
): Readonly<{
  distanceM: number;
  direction: Readonly<{ x: number; z: number }>;
}> | null {
  let nearest: {
    distanceM: number;
    direction: { x: number; z: number };
  } | null = null;
  for (let index = 1; index < line.length; index += 1) {
    const from = line[index - 1];
    const to = line[index];
    if (from === undefined || to === undefined) continue;
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= Number.EPSILON) continue;
    const amount = clamp(
      ((point.x - from.x) * deltaX + (point.z - from.z) * deltaZ) /
        (lengthM * lengthM),
      0,
      1,
    );
    const distanceM = Math.hypot(
      point.x - (from.x + deltaX * amount),
      point.z - (from.z + deltaZ * amount),
    );
    if (nearest === null || distanceM < nearest.distanceM) {
      nearest = {
        distanceM,
        direction: { x: deltaX / lengthM, z: deltaZ / lengthM },
      };
    }
  }
  return nearest;
}

function parkedVehicleFitsTile(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
): boolean {
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  return [
    { longitudinal: halfLength, lateral: halfWidth },
    { longitudinal: halfLength, lateral: -halfWidth },
    { longitudinal: -halfLength, lateral: halfWidth },
    { longitudinal: -halfLength, lateral: -halfWidth },
  ].every((corner) => {
    const x = centre.x + direction.x * corner.longitudinal -
      direction.z * corner.lateral;
    const z = centre.z + direction.z * corner.longitudinal +
      direction.x * corner.lateral;
    return x >= 0 && x <= 256 && z >= 0 && z <= 256;
  });
}

function parkedVehicleFitsRoad(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  road: Readonly<RuntimeLinearFeature>,
  carriagewayWidthM: number,
): boolean {
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  const maximumCentrelineDistanceM = carriagewayWidthM / 2 - 0.05;
  return [
    { longitudinal: halfLength, lateral: halfWidth },
    { longitudinal: halfLength, lateral: -halfWidth },
    { longitudinal: -halfLength, lateral: halfWidth },
    { longitudinal: -halfLength, lateral: -halfWidth },
  ].every((corner) =>
    distanceToPolyline(
      {
        x: centre.x + direction.x * corner.longitudinal -
          direction.z * corner.lateral,
        z: centre.z + direction.z * corner.longitudinal +
          direction.x * corner.lateral,
      },
      road.points,
    ) <= maximumCentrelineDistanceM
  );
}

function parkedVehicleFitsRoadEnvelope(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  road: Readonly<RuntimeLinearFeature>,
  maximumCentrelineDistanceM: number,
): boolean {
  return parkedVehicleCorners(centre, direction).every((corner) =>
    distanceToPolyline(corner, road.points) <= maximumCentrelineDistanceM
  );
}

function longestExteriorParkingEdge(
  feature: Readonly<RuntimeAreaFeature>,
  minimumLengthM: number,
): Readonly<{
  from: Readonly<{ x: number; z: number }>;
  to: Readonly<{ x: number; z: number }>;
}> | null {
  const edges = new Map<
    string,
    { count: number; from: Readonly<{ x: number; z: number }>; to: Readonly<{ x: number; z: number }> }
  >();
  for (const polygon of feature.polygons) {
    const ring = stripClosingPoint(polygon.outer);
    for (let index = 0; index < ring.length; index += 1) {
      const from = ring[index];
      const to = ring[(index + 1) % ring.length];
      if (from === undefined || to === undefined) {
        continue;
      }
      const key = undirectedEdgeKey(from, to);
      const existing = edges.get(key);
      if (existing === undefined) {
        edges.set(key, { count: 1, from, to });
      } else {
        existing.count += 1;
      }
    }
  }
  return [...edges.values()]
    .filter((edge) => edge.count === 1 && !isTileClipEdge(edge.from, edge.to))
    .map((edge) => ({
      from: edge.from,
      to: edge.to,
      lengthM: Math.hypot(edge.to.x - edge.from.x, edge.to.z - edge.from.z),
    }))
    .filter((edge) => edge.lengthM >= minimumLengthM)
    .sort((left, right) => right.lengthM - left.lengthM)[0] ?? null;
}

function undirectedEdgeKey(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): string {
  const first = `${from.x.toFixed(3)},${from.z.toFixed(3)}`;
  const second = `${to.x.toFixed(3)},${to.z.toFixed(3)}`;
  return first < second ? `${first}|${second}` : `${second}|${first}`;
}

function isTileClipEdge(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): boolean {
  return (
    (nearTileBoundary(from.x) && nearTileBoundary(to.x)) ||
    (nearTileBoundary(from.z) && nearTileBoundary(to.z))
  );
}

function nearTileBoundary(value: number): boolean {
  return Math.abs(value) < 0.002 || Math.abs(value - 256) < 0.002;
}

function parkedVehicleFitsFeature(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  feature: Readonly<RuntimeAreaFeature>,
): boolean {
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  return [
    { x: 0, z: 0 },
    { x: halfLength, z: halfWidth },
    { x: halfLength, z: -halfWidth },
    { x: -halfLength, z: halfWidth },
    { x: -halfLength, z: -halfWidth },
  ].every((offset) =>
    pointInAreaFeature(
      {
        x: centre.x + direction.x * offset.x - direction.z * offset.z,
        z: centre.z + direction.z * offset.x + direction.x * offset.z,
      },
      feature,
    )
  );
}

interface ParkingBuildingObstacle {
  readonly footprint: Readonly<PolygonWithHoles>;
  readonly bounds: Readonly<{
    minimumX: number;
    minimumZ: number;
    maximumX: number;
    maximumZ: number;
  }>;
}

interface ParkingAuthoredCollisionObstacle {
  readonly landmark: Readonly<AuthoredLandmarkCollisionSpec>;
  readonly primitive: Readonly<AuthoredLandmarkCollider>;
  readonly centre: Readonly<{ x: number; y: number; z: number }>;
}

interface ParkingRoadVehicleObstacle {
  readonly id: string;
  readonly centre: Readonly<{ x: number; y: number; z: number }>;
  readonly direction: Readonly<{ x: number; z: number }>;
  readonly bodySizeM: Readonly<{ x: number; y: number; z: number }>;
}

interface VehiclePose {
  readonly centre: Readonly<{ x: number; z: number }>;
  readonly direction: Readonly<{ x: number; z: number }>;
}

function parkedVehiclesOverlap(
  first: Readonly<VehiclePose>,
  second: Readonly<VehiclePose>,
): boolean {
  const firstLateral = { x: -first.direction.z, z: first.direction.x };
  const secondLateral = { x: -second.direction.z, z: second.direction.x };
  const delta = {
    x: second.centre.x - first.centre.x,
    z: second.centre.z - first.centre.z,
  };
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  return [
    first.direction,
    firstLateral,
    second.direction,
    secondLateral,
  ].every((axis) => {
    const centreDistance = Math.abs(delta.x * axis.x + delta.z * axis.z);
    const firstExtent = halfLength * Math.abs(
      first.direction.x * axis.x + first.direction.z * axis.z,
    ) + halfWidth * Math.abs(
      firstLateral.x * axis.x + firstLateral.z * axis.z,
    );
    const secondExtent = halfLength * Math.abs(
      second.direction.x * axis.x + second.direction.z * axis.z,
    ) + halfWidth * Math.abs(
      secondLateral.x * axis.x + secondLateral.z * axis.z,
    );
    return centreDistance < firstExtent + secondExtent - 0.02;
  });
}

function parkingBuildingObstacles(
  buildings: readonly Readonly<BuildingFootprintData>[],
): readonly ParkingBuildingObstacle[] {
  return buildings.flatMap((building) => {
    if (building.footprint.outer.length === 0) {
      return [];
    }
    return [{
      footprint: building.footprint,
      bounds: building.footprint.outer.reduce(
        (bounds, point) => ({
          minimumX: Math.min(bounds.minimumX, point.x),
          minimumZ: Math.min(bounds.minimumZ, point.z),
          maximumX: Math.max(bounds.maximumX, point.x),
          maximumZ: Math.max(bounds.maximumZ, point.z),
        }),
        {
          minimumX: Number.POSITIVE_INFINITY,
          minimumZ: Number.POSITIVE_INFINITY,
          maximumX: Number.NEGATIVE_INFINITY,
          maximumZ: Number.NEGATIVE_INFINITY,
        },
      ),
    }];
  });
}

function parkingAuthoredCollisionObstacles(
  tileId: WorldTileId,
  buildings: readonly Readonly<BuildingFootprintData>[],
): readonly ParkingAuthoredCollisionObstacle[] {
  const [tileX, tileZ] = tileId.split(":").map(Number);
  if (!Number.isFinite(tileX) || !Number.isFinite(tileZ)) {
    throw new Error(`Invalid world tile ID ${tileId}`);
  }
  const tileOrigin = { x: tileX! * 256, z: tileZ! * 256 };
  return buildings.flatMap((building) => {
    const landmark = authoredLandmarkCollisionForBuilding(
      tileId,
      building.id,
    );
    if (landmark === null) return [];
    const cosine = Math.cos(landmark.rotationYRadians);
    const sine = Math.sin(landmark.rotationYRadians);
    return landmark.colliders.map((primitive) => ({
      landmark,
      primitive,
      centre: {
        x: landmark.origin.x +
          primitive.centerLocalM.x * cosine +
          primitive.centerLocalM.z * sine - tileOrigin.x,
        y: landmark.origin.y + primitive.centerLocalM.y,
        z: landmark.origin.z -
          primitive.centerLocalM.x * sine +
          primitive.centerLocalM.z * cosine - tileOrigin.z,
      },
    }));
  });
}

function parkingRoadVehicleObstacles(
  tileId: WorldTileId,
): readonly ParkingRoadVehicleObstacle[] {
  const [tileX, tileZ] = tileId.split(":").map(Number);
  if (!Number.isFinite(tileX) || !Number.isFinite(tileZ)) {
    throw new Error(`Invalid world tile ID ${tileId}`);
  }
  const tileOrigin = { x: tileX! * 256, z: tileZ! * 256 };
  return [
    ...staticParkedVehicleCollisionsForTile(tileId).map((spec) =>
      parkingRoadVehicleObstacleFromStatic(spec, tileOrigin)
    ),
    ...VILNIUS_ROAD_VEHICLES.filter((spec) => spec.tileId === tileId).map(
      (spec) => parkingRoadVehicleObstacleFromRoadVehicle(spec, tileOrigin),
    ),
  ];
}

function parkingRoadVehicleObstacleFromStatic(
  spec: Readonly<ParkedVehicleCollisionSpec>,
  tileOrigin: Readonly<{ x: number; z: number }>,
): ParkingRoadVehicleObstacle {
  return {
    id: spec.id,
    centre: {
      x: spec.origin.x - tileOrigin.x,
      y: spec.origin.y,
      z: spec.origin.z - tileOrigin.z,
    },
    direction: {
      x: Math.sin(spec.rotationYRadians),
      z: Math.cos(spec.rotationYRadians),
    },
    bodySizeM: spec.bodySizeM,
  };
}

function parkingRoadVehicleObstacleFromRoadVehicle(
  spec: Readonly<RoadVehicleSpec>,
  tileOrigin: Readonly<{ x: number; z: number }>,
): ParkingRoadVehicleObstacle {
  return {
    id: spec.id,
    centre: {
      x: spec.initialPosition.x - tileOrigin.x,
      y: spec.initialPosition.y,
      z: spec.initialPosition.z - tileOrigin.z,
    },
    direction: {
      x: Math.sin(spec.initialYawRadians),
      z: Math.cos(spec.initialYawRadians),
    },
    bodySizeM: spec.bodySizeM,
  };
}

function parkedVehicleIntersectsRoadVehicles(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  terrain: Readonly<TerrainTileData>,
  obstacles: readonly Readonly<ParkingRoadVehicleObstacle>[],
): boolean {
  if (obstacles.length === 0) return false;
  const groundY = sampleHeight(terrain, centre.x, centre.z) + 0.055;
  const vehicleMinimumY = groundY - 0.04;
  const vehicleMaximumY = groundY + PARKED_VEHICLE_MAXIMUM_BODY_HEIGHT_M + 0.04;
  const vehicleLateral = { x: -direction.z, z: direction.x };
  const vehicleHalfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const vehicleHalfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  return obstacles.some((obstacle) => {
    if (
      vehicleMaximumY < obstacle.centre.y ||
      vehicleMinimumY > obstacle.centre.y + obstacle.bodySizeM.y
    ) {
      return false;
    }
    const obstacleLateral = {
      x: -obstacle.direction.z,
      z: obstacle.direction.x,
    };
    const obstacleHalfLength = obstacle.bodySizeM.z / 2;
    const obstacleHalfWidth = obstacle.bodySizeM.x / 2;
    const delta = {
      x: obstacle.centre.x - centre.x,
      z: obstacle.centre.z - centre.z,
    };
    return [
      direction,
      vehicleLateral,
      obstacle.direction,
      obstacleLateral,
    ].every((axis) => {
      const centreDistanceM = Math.abs(
        delta.x * axis.x + delta.z * axis.z,
      );
      const vehicleExtentM =
        vehicleHalfLength * Math.abs(
          direction.x * axis.x + direction.z * axis.z,
        ) +
        vehicleHalfWidth * Math.abs(
          vehicleLateral.x * axis.x + vehicleLateral.z * axis.z,
        );
      const obstacleExtentM =
        obstacleHalfLength * Math.abs(
          obstacle.direction.x * axis.x + obstacle.direction.z * axis.z,
        ) +
        obstacleHalfWidth * Math.abs(
          obstacleLateral.x * axis.x + obstacleLateral.z * axis.z,
        );
      return centreDistanceM <= vehicleExtentM + obstacleExtentM;
    });
  });
}

function parkedVehicleIntersectsAuthoredCollisions(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  terrain: Readonly<TerrainTileData>,
  obstacles: readonly Readonly<ParkingAuthoredCollisionObstacle>[],
): boolean {
  if (obstacles.length === 0) return false;
  const groundY = sampleHeight(terrain, centre.x, centre.z) + 0.055;
  const vehicleMinimumY = groundY - 0.04;
  const vehicleMaximumY = groundY + PARKED_VEHICLE_MAXIMUM_BODY_HEIGHT_M + 0.04;
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  const lateral = { x: -direction.z, z: direction.x };
  return obstacles.some((obstacle) => {
    const primitive = obstacle.primitive;
    const halfHeight = primitive.kind === "box"
      ? primitive.sizeM.y / 2
      : primitive.heightM / 2;
    if (
      vehicleMaximumY < obstacle.centre.y - halfHeight ||
      vehicleMinimumY > obstacle.centre.y + halfHeight
    ) {
      return false;
    }
    const delta = {
      x: obstacle.centre.x - centre.x,
      z: obstacle.centre.z - centre.z,
    };
    if (primitive.kind === "cylinder") {
      const longitudinal = Math.abs(
        delta.x * direction.x + delta.z * direction.z,
      );
      const crosswise = Math.abs(
        delta.x * lateral.x + delta.z * lateral.z,
      );
      return Math.hypot(
        Math.max(0, longitudinal - halfLength),
        Math.max(0, crosswise - halfWidth),
      ) <= primitive.radiusM;
    }
    const yaw = obstacle.landmark.rotationYRadians +
      (primitive.rotationYRadians ?? 0);
    const obstacleDirection = { x: Math.sin(yaw), z: Math.cos(yaw) };
    const obstacleLateral = {
      x: -obstacleDirection.z,
      z: obstacleDirection.x,
    };
    return [
      direction,
      lateral,
      obstacleDirection,
      obstacleLateral,
    ].every((axis) => {
      const centreDistanceM = Math.abs(
        delta.x * axis.x + delta.z * axis.z,
      );
      const vehicleExtentM =
        halfLength * Math.abs(
          direction.x * axis.x + direction.z * axis.z,
        ) +
        halfWidth * Math.abs(
          lateral.x * axis.x + lateral.z * axis.z,
        );
      const obstacleExtentM =
        primitive.sizeM.z / 2 * Math.abs(
          obstacleDirection.x * axis.x + obstacleDirection.z * axis.z,
        ) +
        primitive.sizeM.x / 2 * Math.abs(
          obstacleLateral.x * axis.x + obstacleLateral.z * axis.z,
        );
      return centreDistanceM <= vehicleExtentM + obstacleExtentM;
    });
  });
}

function parkedVehicleIntersectsBuildings(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  buildings: readonly Readonly<ParkingBuildingObstacle>[],
): boolean {
  const corners = parkedVehicleCorners(centre, direction);
  const minimumX = Math.min(...corners.map((point) => point.x));
  const minimumZ = Math.min(...corners.map((point) => point.z));
  const maximumX = Math.max(...corners.map((point) => point.x));
  const maximumZ = Math.max(...corners.map((point) => point.z));
  return buildings.some((building) => {
    if (
      maximumX < building.bounds.minimumX ||
      minimumX > building.bounds.maximumX ||
      maximumZ < building.bounds.minimumZ ||
      minimumZ > building.bounds.maximumZ
    ) {
      return false;
    }
    return rectangleIntersectsPolygon(
      corners,
      centre,
      direction,
      building.footprint,
    );
  });
}

function parkedVehicleIntersectsTrees(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  trees: readonly Readonly<RuntimeTreeInstance>[],
): boolean {
  return trees.some((tree) => {
    const radiusM = Math.max(
      (tree.trunkDiameterM ?? 0.25) / 2,
      tree.crownRadiusM * TREE_RENDER_CONFIG.trunkRadiusToCrownRatio,
    );
    const deltaX = tree.position.x - centre.x;
    const deltaZ = tree.position.z - centre.z;
    const longitudinal = deltaX * direction.x + deltaZ * direction.z;
    const lateral = -deltaX * direction.z + deltaZ * direction.x;
    return Math.abs(longitudinal) <=
        PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2 + radiusM &&
      Math.abs(lateral) <=
        PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2 + radiusM;
  });
}

function parkedVehicleIntersectsBarriers(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  barriers: readonly Readonly<RuntimeLinearFeature>[],
): boolean {
  return barriers.some((barrier) => {
    const defaultWidthM = barrier.classCode === "hedge"
      ? 0.82
      : barrier.classCode === "fence"
      ? 0.07
      : barrier.classCode === "retaining_wall"
      ? 0.45
      : 0.3;
    const radiusM = clamp((barrier.widthM ?? defaultWidthM) / 2, 0.04, 1.2);
    for (let index = 1; index < barrier.points.length; index += 1) {
      const from = barrier.points[index - 1];
      const to = barrier.points[index];
      if (
        from !== undefined &&
        to !== undefined &&
        segmentIntersectsParkedVehicle(
          from,
          to,
          radiusM,
          centre,
          direction,
        )
      ) {
        return true;
      }
    }
    return false;
  });
}

function segmentIntersectsParkedVehicle(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  radiusM: number,
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
): boolean {
  const toVehicleSpace = (point: Readonly<{ x: number; z: number }>) => {
    const deltaX = point.x - centre.x;
    const deltaZ = point.z - centre.z;
    return {
      longitudinal: deltaX * direction.x + deltaZ * direction.z,
      lateral: -deltaX * direction.z + deltaZ * direction.x,
    };
  };
  const start = toVehicleSpace(from);
  const end = toVehicleSpace(to);
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2 + radiusM;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2 + radiusM;
  let minimum = 0;
  let maximum = 1;
  for (const [origin, delta, extent] of [
    [
      start.longitudinal,
      end.longitudinal - start.longitudinal,
      halfLength,
    ],
    [start.lateral, end.lateral - start.lateral, halfWidth],
  ] as const) {
    if (Math.abs(delta) < 1e-10) {
      if (origin < -extent || origin > extent) {
        return false;
      }
      continue;
    }
    const first = (-extent - origin) / delta;
    const second = (extent - origin) / delta;
    minimum = Math.max(minimum, Math.min(first, second));
    maximum = Math.min(maximum, Math.max(first, second));
    if (minimum > maximum) {
      return false;
    }
  }
  return true;
}

function parkedVehicleCorners(
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
): readonly Readonly<{ x: number; z: number }>[] {
  const halfLength = PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2;
  const halfWidth = PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
  return [
    { longitudinal: halfLength, lateral: halfWidth },
    { longitudinal: halfLength, lateral: -halfWidth },
    { longitudinal: -halfLength, lateral: -halfWidth },
    { longitudinal: -halfLength, lateral: halfWidth },
  ].map((corner) => ({
    x: centre.x + direction.x * corner.longitudinal -
      direction.z * corner.lateral,
    z: centre.z + direction.z * corner.longitudinal +
      direction.x * corner.lateral,
  }));
}

function rectangleIntersectsPolygon(
  corners: readonly Readonly<{ x: number; z: number }>[],
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  polygon: Readonly<PolygonWithHoles>,
): boolean {
  if (corners.some((point) => pointInPolygon(point, polygon))) {
    return true;
  }
  if (polygon.outer.some((point) => pointInParkedVehicle(
    point,
    centre,
    direction,
  ))) {
    return true;
  }
  const vehicleEdges = corners.map((from, index) => ({
    from,
    to: corners[(index + 1) % corners.length]!,
  }));
  return [polygon.outer, ...polygon.holes].some((ring) =>
    ring.some((from, index) => {
      const to = ring[(index + 1) % ring.length];
      return to !== undefined && vehicleEdges.some((edge) =>
        segmentsIntersect(edge.from, edge.to, from, to)
      );
    })
  );
}

function pointInPolygon(
  point: Readonly<{ x: number; z: number }>,
  polygon: Readonly<PolygonWithHoles>,
): boolean {
  return pointInRing(point, polygon.outer) &&
    !polygon.holes.some((hole) => pointInRing(point, hole));
}

function pointInParkedVehicle(
  point: Readonly<{ x: number; z: number }>,
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
): boolean {
  const deltaX = point.x - centre.x;
  const deltaZ = point.z - centre.z;
  const longitudinal = deltaX * direction.x + deltaZ * direction.z;
  const lateral = -deltaX * direction.z + deltaZ * direction.x;
  return Math.abs(longitudinal) <= PARKED_VEHICLE_CLEARANCE_LENGTH_M / 2 &&
    Math.abs(lateral) <= PARKED_VEHICLE_CLEARANCE_WIDTH_M / 2;
}

function segmentsIntersect(
  a: Readonly<{ x: number; z: number }>,
  b: Readonly<{ x: number; z: number }>,
  c: Readonly<{ x: number; z: number }>,
  d: Readonly<{ x: number; z: number }>,
): boolean {
  const abC = cross2d(a, b, c);
  const abD = cross2d(a, b, d);
  const cdA = cross2d(c, d, a);
  const cdB = cross2d(c, d, b);
  const epsilon = 1e-8;
  if (
    ((abC > epsilon && abD < -epsilon) ||
      (abC < -epsilon && abD > epsilon)) &&
    ((cdA > epsilon && cdB < -epsilon) ||
      (cdA < -epsilon && cdB > epsilon))
  ) {
    return true;
  }
  return (
    (Math.abs(abC) <= epsilon && pointOnSegment(c, a, b, epsilon)) ||
    (Math.abs(abD) <= epsilon && pointOnSegment(d, a, b, epsilon)) ||
    (Math.abs(cdA) <= epsilon && pointOnSegment(a, c, d, epsilon)) ||
    (Math.abs(cdB) <= epsilon && pointOnSegment(b, c, d, epsilon))
  );
}

function cross2d(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  point: Readonly<{ x: number; z: number }>,
): number {
  return (to.x - from.x) * (point.z - from.z) -
    (to.z - from.z) * (point.x - from.x);
}

function pointOnSegment(
  point: Readonly<{ x: number; z: number }>,
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  epsilon: number,
): boolean {
  return point.x >= Math.min(from.x, to.x) - epsilon &&
    point.x <= Math.max(from.x, to.x) + epsilon &&
    point.z >= Math.min(from.z, to.z) - epsilon &&
    point.z <= Math.max(from.z, to.z) + epsilon;
}

function pointInAreaFeature(
  point: Readonly<{ x: number; z: number }>,
  feature: Readonly<RuntimeAreaFeature>,
): boolean {
  return feature.polygons.some((polygon) =>
    pointInRing(point, polygon.outer) &&
    !polygon.holes.some((hole) => pointInRing(point, hole))
  );
}

function pointInRing(
  point: Readonly<{ x: number; z: number }>,
  ring: readonly Readonly<{ x: number; z: number }>[],
): boolean {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
    const a = ring[index];
    const b = ring[previous];
    if (a === undefined || b === undefined) {
      continue;
    }
    const crosses = (a.z > point.z) !== (b.z > point.z) &&
      point.x < (b.x - a.x) * (point.z - a.z) / (b.z - a.z) + a.x;
    if (crosses) {
      inside = !inside;
    }
  }
  return inside;
}

function hashUnit(value: string): number {
  return hashInteger(value) / 4_294_967_296;
}

function hashInteger(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function appendOrientedBox(
  positions: number[],
  indices: number[],
  centre: Readonly<{ x: number; z: number }>,
  direction: Readonly<{ x: number; z: number }>,
  widthM: number,
  lengthM: number,
  baseY: number,
  heightM: number,
  colours?: number[],
  colour?: readonly [number, number, number],
  longitudinalGroundSlope = 0,
): void {
  const halfWidth = widthM / 2;
  const halfLength = lengthM / 2;
  const lateral = { x: -direction.z, z: direction.x };
  const corners = [
    { longitudinal: -halfLength, lateral: -halfWidth },
    { longitudinal: -halfLength, lateral: halfWidth },
    { longitudinal: halfLength, lateral: -halfWidth },
    { longitudinal: halfLength, lateral: halfWidth },
  ].map((corner) => ({
    x: centre.x + direction.x * corner.longitudinal +
      lateral.x * corner.lateral,
    z: centre.z + direction.z * corner.longitudinal +
      lateral.z * corner.lateral,
    longitudinal: corner.longitudinal,
  }));
  const base = positions.length / 3;
  for (const heightOffsetM of [0, heightM]) {
    for (const corner of corners) {
      positions.push(
        corner.x,
        baseY + heightOffsetM +
          longitudinalGroundSlope * corner.longitudinal,
        corner.z,
      );
      if (colours !== undefined && colour !== undefined) {
        colours.push(...colour);
      }
    }
  }
  indices.push(
    base, base + 4, base + 1, base + 1, base + 4, base + 5,
    base + 2, base + 3, base + 6, base + 3, base + 7, base + 6,
    base, base + 2, base + 4, base + 2, base + 6, base + 4,
    base + 1, base + 5, base + 3, base + 3, base + 5, base + 7,
    base + 4, base + 6, base + 5, base + 5, base + 6, base + 7,
  );
}

export function hardscapeMaterialGroup(
  classCode: string,
): HardscapeMaterialGroup {
  const normalized = classCode.toLowerCase();
  if (normalized.includes(":crossing-marking:")) {
    return "marking";
  }
  if (
    /:(?:unpaved|ground|dirt|earth|sand|gravel|fine_gravel|clay|compacted|grass_paver)$/u
      .test(normalized)
  ) {
    return "unpaved";
  }
  if (
    normalized.includes(":parking:") ||
    normalized.includes(":service:") ||
    normalized.includes(":residential:") ||
    normalized.includes(":yes:")
  ) {
    return "carriageway";
  }
  return "pedestrian";
}

function createHardscapeGeometry(
  features: readonly Readonly<RuntimeAreaFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const liftM = feature.classCode.toLowerCase().includes(
        ":crossing-marking:"
      )
      ? ROAD_MARKING_LIFT_M
      : SURFACE_LIFT_M;
    for (const polygon of feature.polygons) {
      const outer = stripClosingPoint(polygon.outer);
      const holes = polygon.holes.map(stripClosingPoint);
      if (outer.length < 3) {
        continue;
      }
      const faces = ShapeUtils.triangulateShape(
        outer.map((point) => new Vector2(point.x, point.z)),
        holes.map((hole) =>
          hole.map((point) => new Vector2(point.x, point.z))
        ),
      );
      const points = [...outer, ...holes.flat()];
      const base = positions.length / 3;
      for (const point of points) {
        positions.push(
          point.x,
          sampleHeight(terrain, point.x, point.z) + liftM,
          point.z,
        );
      }
      for (const face of faces) {
        const [first, second, third] = face;
        if (
          first !== undefined &&
          second !== undefined &&
          third !== undefined
        ) {
          const a = points[first]!;
          const b = points[second]!;
          const c = points[third]!;
          const signedAreaXz =
            (b.x - a.x) * (c.z - a.z) -
            (b.z - a.z) * (c.x - a.x);
          if (signedAreaXz > 0) {
            indices.push(base + first, base + third, base + second);
          } else {
            indices.push(base + first, base + second, base + third);
          }
        }
      }
    }
  }
  return geometryFrom(positions, indices);
}

function createStepGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const widthM = clamp(feature.widthM ?? 2.2, 1, 6);
    forEachDensifiedSegment(feature, 0.8, (from, to) => {
      const dx = to.x - from.x;
      const dz = to.z - from.z;
      const lengthM = Math.hypot(dx, dz);
      if (lengthM < 0.02) {
        return;
      }
      const nx = -dz / lengthM * widthM / 2;
      const nz = dx / lengthM * widthM / 2;
      const fromY = sampleHeight(terrain, from.x, from.z) + SURFACE_LIFT_M;
      const toY = sampleHeight(terrain, to.x, to.z) + SURFACE_LIFT_M;
      const base = positions.length / 3;
      positions.push(
        from.x + nx, fromY, from.z + nz,
        from.x - nx, fromY, from.z - nz,
        to.x + nx, toY, to.z + nz,
        to.x - nx, toY, to.z - nz,
      );
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    });
  }
  return geometryFrom(positions, indices);
}

function createBarrierGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const defaults = barrierDimensions(feature.classCode);
    const widthM = clamp(feature.widthM ?? defaults.widthM, 0.08, 2.5);
    const heightM = clamp(feature.heightM ?? defaults.heightM, 0.25, 12);
    forEachDensifiedSegment(feature, 4, (from, to) => {
      appendSegmentPrism(
        positions,
        indices,
        from,
        to,
        sampleHeight(terrain, from.x, from.z) - STRUCTURE_BASE_SINK_M,
        sampleHeight(terrain, to.x, to.z) - STRUCTURE_BASE_SINK_M,
        widthM,
        heightM,
      );
    });
  }
  return geometryFrom(positions, indices);
}

function createFenceGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const heightM = clamp(feature.heightM ?? 1.35, 0.5, 4);
    const railWidthM = clamp(feature.widthM ?? 0.07, 0.04, 0.2);
    for (let index = 1; index < feature.points.length; index += 1) {
      if (
        isSuppressedGelezinioVilkoGenericStructureSegment(
          feature.id,
          index - 1,
        )
      ) {
        continue;
      }
      const from = feature.points[index - 1];
      const to = feature.points[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
      if (lengthM < 0.02) {
        continue;
      }
      const sections = Math.max(1, Math.ceil(lengthM / 3));
      for (let section = 0; section <= sections; section += 1) {
        const factor = section / sections;
        const x = from.x + (to.x - from.x) * factor;
        const z = from.z + (to.z - from.z) * factor;
        appendAxisBox(
          positions,
          indices,
          x,
          sampleHeight(terrain, x, z) - STRUCTURE_BASE_SINK_M,
          z,
          Math.max(0.09, railWidthM * 1.6),
          heightM,
        );
      }
      const fromGround = sampleHeight(terrain, from.x, from.z);
      const toGround = sampleHeight(terrain, to.x, to.z);
      for (const railFactor of [0.35, 0.82]) {
        appendSegmentPrism(
          positions,
          indices,
          from,
          to,
          fromGround + heightM * railFactor,
          toGround + heightM * railFactor,
          railWidthM,
          Math.max(0.06, railWidthM),
        );
      }
    }
  }
  return geometryFrom(positions, indices);
}

function createHedgeGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const heightM = clamp(feature.heightM ?? 1.35, 0.45, 3.5);
    const widthM = clamp(feature.widthM ?? 0.82, 0.35, 2.4);
    forEachDensifiedSegment(feature, 2.4, (from, to) => {
      const fromGround = sampleHeight(terrain, from.x, from.z) -
        STRUCTURE_BASE_SINK_M;
      const toGround = sampleHeight(terrain, to.x, to.z) -
        STRUCTURE_BASE_SINK_M;
      appendSegmentPrism(
        positions,
        indices,
        from,
        to,
        fromGround,
        toGround,
        widthM,
        heightM * 0.72,
      );
      appendSegmentPrism(
        positions,
        indices,
        from,
        to,
        fromGround + heightM * 0.48,
        toGround + heightM * 0.48,
        widthM * 0.72,
        heightM * 0.52,
      );
    });
  }
  return geometryFrom(positions, indices);
}

function createTrolleyWireGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  bridgeCollisions: readonly Readonly<AuthoredBridgeCollisionSpec>[],
  tileOrigin: Readonly<{ x: number; z: number }>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  const tileExtentM = (terrain.samplesPerSide - 1) * terrain.spacingM;
  const tileCentre = {
    x: tileOrigin.x + tileExtentM * 0.5,
    z: tileOrigin.z + tileExtentM * 0.5,
  };
  const bridgeSurfaces = bridgeCollisions.flatMap((bridge) => {
    const maximumLocalXM = bridge.segments.reduce(
      (maximum, segment) => Math.max(
        maximum,
        Math.abs(segment.startLocalXM),
        Math.abs(segment.endLocalXM),
      ),
      0,
    );
    const maximumReachM = maximumLocalXM + bridge.widthM * 0.5 +
      Math.SQRT2 * tileExtentM * 0.5 + 1;
    if (
      Math.hypot(
        bridge.position.x - tileCentre.x,
        bridge.position.z - tileCentre.z,
      ) > maximumReachM
    ) {
      return [];
    }
    return [{
      bridge,
      cosine: Math.cos(bridge.rotationYRadians),
      sine: Math.sin(bridge.rotationYRadians),
    }];
  });
  for (const feature of features) {
    forEachDensifiedSegment(feature, 11, (from, to) => {
      const dx = to.x - from.x;
      const dz = to.z - from.z;
      const lengthM = Math.hypot(dx, dz);
      if (lengthM < 0.02) {
        return;
      }
      const lateral = { x: -dz / lengthM * 0.38, z: dx / lengthM * 0.38 };
      for (const side of [-1, 1]) {
        const wireFrom = {
          x: from.x + lateral.x * side,
          z: from.z + lateral.z * side,
        };
        const wireTo = {
          x: to.x + lateral.x * side,
          z: to.z + lateral.z * side,
        };
        appendSegmentPrism(
          positions,
          indices,
          wireFrom,
          wireTo,
          sampleTrolleyWireSupportHeight(
            terrain,
            bridgeSurfaces,
            tileOrigin,
            wireFrom,
          ) + 5.62,
          sampleTrolleyWireSupportHeight(
            terrain,
            bridgeSurfaces,
            tileOrigin,
            wireTo,
          ) + 5.62,
          0.035,
          0.035,
        );
      }
    });
  }
  return geometryFrom(positions, indices);
}

/**
 * Uses the same authored bridge traversal surface as physics whenever a wire
 * crosses a named bridge. Bare terrain remains authoritative elsewhere.
 */
function sampleTrolleyWireSupportHeight(
  terrain: Readonly<TerrainTileData>,
  bridgeSurfaces: readonly Readonly<{
    bridge: Readonly<AuthoredBridgeCollisionSpec>;
    cosine: number;
    sine: number;
  }>[],
  tileOrigin: Readonly<{ x: number; z: number }>,
  point: Readonly<{ x: number; z: number }>,
): number {
  let surfaceM = sampleHeight(terrain, point.x, point.z);
  const worldX = tileOrigin.x + point.x;
  const worldZ = tileOrigin.z + point.z;
  for (const { bridge, cosine, sine } of bridgeSurfaces) {
    const dx = worldX - bridge.position.x;
    const dz = worldZ - bridge.position.z;
    const localX = cosine * dx - sine * dz;
    const localZ = sine * dx + cosine * dz;
    if (Math.abs(localZ) > bridge.widthM * 0.5 + 0.5) {
      continue;
    }
    const segment = bridge.segments.find(
      (candidate) =>
        localX >= candidate.startLocalXM && localX <= candidate.endLocalXM,
    );
    if (segment === undefined) {
      continue;
    }
    const progress = (localX - segment.startLocalXM) /
      (segment.endLocalXM - segment.startLocalXM);
    const bridgeSurfaceM = segment.startSurfaceWorldM +
      (segment.endSurfaceWorldM - segment.startSurfaceWorldM) * progress;
    surfaceM = Math.max(surfaceM, bridgeSurfaceM);
  }
  return surfaceM;
}

function createParcelBoundaryGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];
  const seenSegments = new Set<string>();
  for (const feature of features) {
    forEachDensifiedSegment(feature, 8, (from, to) => {
      const key = undirectedEdgeKey(from, to);
      if (seenSegments.has(key)) {
        return;
      }
      seenSegments.add(key);
      appendSegmentPrism(
        positions,
        indices,
        from,
        to,
        sampleHeight(terrain, from.x, from.z) + 0.018,
        sampleHeight(terrain, to.x, to.z) + 0.018,
        0.038,
        0.014,
      );
    });
  }
  return geometryFrom(positions, indices);
}

function appendSegmentPrism(
  positions: number[],
  indices: number[],
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  fromBaseY: number,
  toBaseY: number,
  widthM: number,
  heightM: number,
): void {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const lengthM = Math.hypot(dx, dz);
  if (lengthM < 0.02) {
    return;
  }
  const nx = -dz / lengthM * widthM / 2;
  const nz = dx / lengthM * widthM / 2;
  const base = positions.length / 3;
  positions.push(
    from.x + nx, fromBaseY, from.z + nz,
    from.x - nx, fromBaseY, from.z - nz,
    to.x + nx, toBaseY, to.z + nz,
    to.x - nx, toBaseY, to.z - nz,
    from.x + nx, fromBaseY + heightM, from.z + nz,
    from.x - nx, fromBaseY + heightM, from.z - nz,
    to.x + nx, toBaseY + heightM, to.z + nz,
    to.x - nx, toBaseY + heightM, to.z - nz,
  );
  indices.push(
    base, base + 2, base + 4, base + 2, base + 6, base + 4,
    base + 1, base + 5, base + 3, base + 3, base + 5, base + 7,
    base + 4, base + 6, base + 5, base + 5, base + 6, base + 7,
    base, base + 4, base + 1, base + 1, base + 4, base + 5,
    base + 2, base + 3, base + 6, base + 3, base + 7, base + 6,
  );
}

function appendAxisBox(
  positions: number[],
  indices: number[],
  x: number,
  baseY: number,
  z: number,
  widthM: number,
  heightM: number,
): void {
  const half = widthM / 2;
  const base = positions.length / 3;
  positions.push(
    x - half, baseY, z - half,
    x + half, baseY, z - half,
    x - half, baseY, z + half,
    x + half, baseY, z + half,
    x - half, baseY + heightM, z - half,
    x + half, baseY + heightM, z - half,
    x - half, baseY + heightM, z + half,
    x + half, baseY + heightM, z + half,
  );
  indices.push(
    base, base + 4, base + 1, base + 1, base + 4, base + 5,
    base + 2, base + 3, base + 6, base + 3, base + 7, base + 6,
    base, base + 2, base + 4, base + 2, base + 6, base + 4,
    base + 1, base + 5, base + 3, base + 3, base + 5, base + 7,
    base + 4, base + 6, base + 5, base + 5, base + 6, base + 7,
  );
}

function forEachDensifiedSegment(
  feature: Readonly<RuntimeLinearFeature>,
  maximumLengthM: number,
  callback: (
    from: Readonly<{ x: number; z: number }>,
    to: Readonly<{ x: number; z: number }>,
  ) => void,
): void {
  for (let index = 1; index < feature.points.length; index += 1) {
    const from = feature.points[index - 1];
    const to = feature.points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
    const sections = Math.max(1, Math.ceil(lengthM / maximumLengthM));
    for (let section = 0; section < sections; section += 1) {
      const fromFactor = section / sections;
      const toFactor = (section + 1) / sections;
      callback(
        {
          x: from.x + (to.x - from.x) * fromFactor,
          z: from.z + (to.z - from.z) * fromFactor,
        },
        {
          x: from.x + (to.x - from.x) * toFactor,
          z: from.z + (to.z - from.z) * toFactor,
        },
      );
    }
  }
}

function barrierDimensions(classCode: string): Readonly<{
  widthM: number;
  heightM: number;
}> {
  switch (classCode) {
    case "kerb":
      return { widthM: 0.18, heightM: 0.14 };
    case "retaining_wall":
      return { widthM: 0.45, heightM: 1.5 };
    case "cliff":
      return { widthM: 0.55, heightM: 2.2 };
    case "embankment":
      return { widthM: 0.5, heightM: 0.9 };
    default:
      return { widthM: 0.3, heightM: 1.8 };
  }
}

function sampleHeight(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): number {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  return sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    {
      x: clamp(x, 0, maximum),
      z: clamp(z, 0, maximum),
    },
  ) ?? 0;
}

function stripClosingPoint<T extends Readonly<{ x: number; z: number }>>(
  ring: readonly T[],
): readonly T[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first !== undefined && last !== undefined &&
      first.x === last.x && first.z === last.z
    ? ring.slice(0, -1)
    : ring;
}

function geometryFrom(
  positions: readonly number[],
  indices: readonly number[],
  colours?: readonly number[],
): BufferGeometry | null {
  if (positions.length === 0 || indices.length === 0) {
    return null;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex([...indices]);
  if (colours !== undefined) {
    geometry.setAttribute("color", new Float32BufferAttribute(colours, 3));
  }
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function addMesh(
  group: Group,
  geometry: BufferGeometry | null,
  material: MeshStandardMaterial,
  name: string,
  receiveShadow: boolean,
): void {
  if (geometry === null) {
    return;
  }
  const mesh = new Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = !receiveShadow;
  mesh.receiveShadow = receiveShadow;
  group.add(mesh);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
